// src/components/properties/PropertySettingsTab.js
import React, { useState, useEffect } from 'react';
import { Save, Loader2, AlertTriangle } from 'lucide-react';
import { api } from '../../api';
import toast from 'react-hot-toast';

const PropertySettingsTab = ({ property, onUpdate }) => {
    const [isEnabled, setIsEnabled] = useState(false);
    const [threshold, setThreshold] = useState(8);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (property) {
            setIsEnabled(property.settings_overtime_enabled === 1 || property.settings_overtime_enabled === true);
            setThreshold(property.settings_overtime_threshold || 8);
        }
    }, [property]);

    const handleSave = async () => {
        // --- VALIDATION LOGIC ---
        const val = parseFloat(threshold);

        // 4. Check for non-numbers
        if (isNaN(val)) {
            toast.error("Please enter a valid number for the threshold.");
            return;
        }

        // 2. Logic: If 0, we treat it as disabling/deleting
        if (val === 0) {
            // We allow 0, but it will trigger the delete logic in the API
            // No error needed here, code below handles it.
        } 
        // 1. Validation: No less than 1 hour (unless 0)
        else if (val < 1) {
            toast.error("Threshold cannot be less than 1 hour.");
            return;
        }
        // 3. Validation: No more than 8 hours
        else if (val > 8) {
            toast.error("Threshold cannot be more than 8 hours.");
            return;
        }

        setIsSaving(true);
        try {
            // If user entered 0, we force enabled to false to trigger cleanup
            const finalEnabled = val === 0 ? false : isEnabled;

            await api.updatePropertySettings(property.id, {
                is_overtime_enabled: finalEnabled,
                overtime_threshold: val
            });
            
            if (val === 0) {
                toast.success("Settings removed (Value was 0).");
                setIsEnabled(false); // Update UI to match
                setThreshold(8); // Reset UI to default
            } else {
                toast.success("Settings configuration saved.");
            }
            
            if (onUpdate) onUpdate(); 
        } catch (error) {
            toast.error(error.message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="property-settings-tab">
            <div className="tab-content-header">
                 <h3 className="tab-content-title">Configuration</h3>
            </div>
            
            <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px' }}>
                <h4 style={{ margin: '0 0 15px 0', fontSize: '1rem', color: '#334155', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                    Overtime Rules
                </h4>
                
                <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '15px' }}>
                        <input 
                            type="checkbox" 
                            checked={isEnabled} 
                            onChange={(e) => setIsEnabled(e.target.checked)} 
                            style={{ width: '18px', height: '18px', accentColor: '#1798c1' }}
                        />
                        <span style={{ fontWeight: '500', fontSize: '0.95rem', color: '#334155' }}>Enable Overtime Calculation</span>
                    </label>
                </div>

                {isEnabled && (
                    <div className="form-group" style={{ maxWidth: '300px' }}>
                        <label className="form-label">Daily Threshold (Hours)</label>
                        <input 
                            type="number" 
                            step="0.5"
                            min="0"
                            max="8"
                            className="form-input" 
                            value={threshold} 
                            onChange={(e) => setThreshold(e.target.value)} 
                        />
                        <small style={{ color: '#64748b', display:'block', marginTop:'6px', fontSize: '0.85rem' }}>
                            Max 8 hours. Set to <b>0</b> to remove this configuration.
                        </small>
                        
                        {/* Visual Warnings based on input */}
                        {parseFloat(threshold) > 8 && (
                            <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '5px', display:'flex', alignItems:'center', gap:'5px'}}>
                                <AlertTriangle size={12}/> Value cannot exceed 8 hours.
                            </div>
                        )}
                    </div>
                )}

                <div style={{ marginTop: '25px', paddingTop: '15px', borderTop: '1px solid #f1f5f9' }}>
                    <button onClick={handleSave} className="button action-button" disabled={isSaving}>
                        {isSaving ? <Loader2 className="spin" size={16} /> : <Save size={16} />}
                        <span style={{ marginLeft: '8px' }}>{isSaving ? 'Saving...' : 'Save Configuration'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PropertySettingsTab;